import { ProfileImage } from 'components/layout/misc/ProfileImage';
import React, { useCallback, useEffect, useState } from 'react';
import { modalStore } from 'components/layout/modals/ModalStore';
import { AddJobModal } from '../pipeline/components/AddJobModal';
import { applicantStore } from './ApplicantStore';
import localStorage from 'local-storage';
import { ApplicantHistory } from './components/ApplicantHistory';
import GroupByPositionToggle from './components/GroupByPositionToggle';
import { ApplicantInternalProcess } from './components/ApplicantInternalProcess';
import { ApplicantTaskCreate } from './components/ApplicantTaskCreate';
import { ApplicantChangelog } from './components/ApplicantChangelog';
import PinnedPanelToggleButton from 'ats/components/pinned-tasks-panel/PinnedPanelToggleButton';
import { PIN_SCOPES } from 'ats/utils/taskPin';
import HoverMenu from 'ats/components/hover-menu/HoverMenu';
import { candidatesStore } from 'components/candidates/CandidatesStore';
import { eventManager } from 'ats/utils/EventManager';
import ApplicantPublicProfile, {
	PUBLIC_PROFILE_STATUS,
	createPublicProfileForApplicant,
	getPublicProfileStatus
} from './components/ApplicantPublicProfile';
import { ApplicantEdit } from './components/ApplicantEdit';
import {
	candidateHiringOptions,
	candidateSeniorityOptionLabel
} from 'components/candidates/profile/scorecards/CandidateScorecardStore';
import UserProfileAvatar from 'ats/components/user-profile-avatar/UserProfileAvatar';
import { drawerStore } from 'ats/components/drawer/DrawerStore';
import { formatTimeAgo } from 'shared/utils';
import ActionMenu from 'ats/components/action-menu/ActionMenu';
import { copyTextToClipboard } from 'ats/utils/copyToClipboard';
import ApplicantStageIndicator from 'ats/components/applicant-stage-indicator/ApplicantStageIndicator';
import PendingTaskBadge from 'ats/components/pending-task-badge/PendingTaskBadge';
import ApplicantResumeUpload from './components/ApplicantResumeUpload';
import ApplicantResumeHistory from './components/ApplicantResumeHistory';
import { notificationStore } from 'components/layout/notifications/NotificationStore';
import { offerJobApplicationCascade } from 'ats/utils/applicantStageCascade';
import { PermissionGate } from 'shared/security/PermissionGate';
import {
	PERMISSIONS,
	getApplicantProfileTabIndex,
	hasPermission,
	isApplicantProfileTabAllowed
} from 'shared/security/rolePermissions';

const TABS = {
	HISTORY: 0,
	INTERNAL_PROCESS: 1,
	PROFILE: 2,
	CHANGELOG: 3
};

const TAB_INDEX_TO_ID = {
	[TABS.HISTORY]: 'history',
	[TABS.INTERNAL_PROCESS]: 'internal_process',
	[TABS.PROFILE]: 'profile',
	[TABS.CHANGELOG]: 'changelog'
};

const resolveAllowedTab = tab => {
	const tabId = TAB_INDEX_TO_ID[tab];
	if (tabId && !isApplicantProfileTabAllowed(tabId)) {
		return getApplicantProfileTabIndex();
	}
	return tab;
};

const TAB_SECTION_TITLES = {
	[TABS.HISTORY]: 'Job History',
	[TABS.INTERNAL_PROCESS]: 'Internal Process',
	[TABS.PROFILE]: 'Profile',
	[TABS.CHANGELOG]: 'Log'
};

const JOB_HISTORY_GROUP_BY_POSITION_KEY = 'applicantJobHistoryGroupByPosition';

const toPendingCount = value => {
	const count = Number(value);
	return Number.isFinite(count) && count > 0 ? count : 0;
};

const getJobHistoryPendingCount = applicant =>
	(applicant?.jobApplications ?? []).reduce(
		(sum, jobApplication) =>
			sum + toPendingCount(jobApplication.pendingTasksCount),
		0
	);

export default function ApplicantProfile({
	onClose,
	onSuccess,
	applicantId,
	jobApplicationId,
	initialTab
}) {
	const [activeTab, setActiveTab] = useState(() =>
		resolveAllowedTab(initialTab ?? getApplicantProfileTabIndex())
	);
	const [data, setData] = useState(null);
	const [groupJobHistoryByPosition, setGroupJobHistoryByPosition] = useState(
		() => localStorage.get(JOB_HISTORY_GROUP_BY_POSITION_KEY) !== false
	);

	const handleGroupJobHistoryByPositionChange = value => {
		setGroupJobHistoryByPosition(value);
		localStorage.set(JOB_HISTORY_GROUP_BY_POSITION_KEY, value);
	};

	const loadData = async id => {
		const result = await applicantStore.get(id);

		setData(result.data);
	};

	useEffect(() => {
		loadData(applicantId);
	}, [applicantId]);

	useEffect(() => {
		if (initialTab != null) {
			setActiveTab(resolveAllowedTab(initialTab));
		}
	}, [initialTab, applicantId]);

	useEffect(() => {
		const allowedTab = resolveAllowedTab(activeTab);
		if (allowedTab !== activeTab) {
			setActiveTab(allowedTab);
		}
	}, [activeTab]);

	const handleJobApplicationStageChanged = useCallback(
		({ applicantId } = {}) => {
			if (String(applicantId) === String(data?.id)) {
				loadData(data.id);
			}
		},
		[data?.id]
	);

	const handleJobApplicationDeleted = useCallback(
		({ applicantId } = {}) => {
			if (String(applicantId) === String(data?.id)) {
				loadData(data.id);
			}
		},
		[data?.id]
	);

	const handleTaskChanged = useCallback(() => {
		if (data?.id) {
			loadData(data.id);
		}
	}, [data?.id]);

	useEffect(() => {
		eventManager.on(
			'jobApplicationStageChanged',
			handleJobApplicationStageChanged
		);

		eventManager.on('jobApplicationDeleted', handleJobApplicationDeleted);

		eventManager.on('applicantStageChanged', handleJobApplicationStageChanged);

		eventManager.on('taskChanged', handleTaskChanged);

		return () => {
			eventManager.off(
				'jobApplicationStageChanged',
				handleJobApplicationStageChanged
			);

			eventManager.off('jobApplicationDeleted', handleJobApplicationDeleted);

			eventManager.off(
				'applicantStageChanged',
				handleJobApplicationStageChanged
			);

			eventManager.off('taskChanged', handleTaskChanged);
		};
	}, [
		handleJobApplicationStageChanged,
		handleJobApplicationDeleted,
		handleTaskChanged
	]);

	const handleTabClick = (e, tabId) => {
		e.preventDefault();
		if (activeTab === tabId) {
			return;
		}
		setActiveTab(tabId);
	};

	const internalProcessPendingCount = toPendingCount(data?.pendingTasksCount);
	const jobHistoryPendingCount = getJobHistoryPendingCount(data);

	const handleSuccess = () => {
		onSuccess && onSuccess();
		loadData(applicantId);
	};

	const openApplicantEditModal = initialFocusField => {
		modalStore.openModal(
			{
				title: 'Edit Candidate',
				size: '2xl',
				position: 'center',
				saveButtonLabel: 'Save'
			},
			<ApplicantEdit
				applicant={data}
				handleSuccess={handleSuccess}
				initialFocusField={initialFocusField}
			/>
		);
	};

	const assignApplicantToJob = () => {
		modalStore.openModal(
			{ title: 'Assign to an Open Job', size: 'xl', position: 'center' },
			<AddJobModal applicantId={applicantId} handleSuccess={handleSuccess} />
		);
	};

	const createTaskForApplicant = () => {
		modalStore.openModal(
			{ title: 'Add Task', size: 'xl', position: 'center' },
			<ApplicantTaskCreate handleSuccess={handleSuccess} applicant={data} />
		);
	};

	const handleViewResume = async resume => {
		try {
			const downloadResult = await applicantStore.downloadResume(
				applicantId,
				resume.id
			);
			const blob = new Blob([downloadResult], {
				type: resume.mimeType || 'application/pdf'
			});
			const url = URL.createObjectURL(blob);
			window.open(url, '_blank');
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Error',
				'Failed to view resume.'
			);
		}
	};

	const handleDownloadResume = async resume => {
		try {
			const downloadResult = await applicantStore.downloadResume(
				applicantId,
				resume.id
			);
			const blob = new Blob([downloadResult], {
				type: resume.mimeType || 'application/pdf'
			});
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = resume.fileName || 'resume.pdf';
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch (error) {
			notificationStore.pushErrorNotification(
				'Error',
				'Failed to download resume.'
			);
		}
	};

	const actionButtons = {
		[TABS.HISTORY]: [
			{
				permission: PERMISSIONS.APPLICANT_ASSIGN_JOB,
				label: 'Assign to a Job',
				onClick: assignApplicantToJob
			}
		],
		[TABS.INTERNAL_PROCESS]: [
			{
				permission: PERMISSIONS.APPLICANT_TASK_CREATE,
				label: (
					<>
						<img src="/ats/icons/plus-blue-icon.svg" alt="Add Task" /> Add Task
					</>
				),
				onClick: createTaskForApplicant
			},
			{
				permission: PERMISSIONS.APPLICANT_ASSIGN_JOB,
				label: 'Assign to a Job',
				onClick: assignApplicantToJob
			}
		],
		[TABS.PROFILE]: [
			...(data?.candidate?.id
				? [
						{
							permission: PERMISSIONS.CANDIDATE_PROFILE_EDIT,
							iconOnly: true,
							iconSrc: '/ats/icons/pencil-blue-icon.svg',
							ariaLabel: 'Edit profile',
							onClick: () => {
								window.open(
									`/ats/candidates/profile/${data.candidate.id}/edit`,
									'_blank'
								);
							}
						}
					]
				: []),
			...(data && getPublicProfileStatus(data) === PUBLIC_PROFILE_STATUS.NONE
				? [
						{
							permission: PERMISSIONS.APPLICANT_EDIT,
							label: 'Create Public Profile',
							onClick: () =>
								createPublicProfileForApplicant(data, handleSuccess)
						}
					]
				: []),
			{
				permission: PERMISSIONS.APPLICANT_ASSIGN_JOB,
				label: 'Assign to a Job',
				onClick: assignApplicantToJob
			}
		],
		[TABS.CHANGELOG]: [
			{
				permission: PERMISSIONS.APPLICANT_ASSIGN_JOB,
				label: 'Assign to a Job',
				onClick: assignApplicantToJob
			}
		]
	};

	const getActionButtonsForTab = tab =>
		(actionButtons[tab] || []).filter(button =>
			hasPermission(button.permission)
		);

	const renderTabActionButtons = tabId =>
		getActionButtonsForTab(tabId).map((button, index) => {
			if (button.iconOnly) {
				return (
					<button
						key={`${tabId}-${index}`}
						type="button"
						className="ats-button-soft ats-button-soft--icon-only"
						aria-label={button.ariaLabel}
						onClick={button.onClick}>
						<img src={button.iconSrc} alt="" width={16} height={16} />
					</button>
				);
			}
			return (
				<button
					key={`${tabId}-${index}`}
					type="button"
					className="ats-button-soft"
					onClick={button.onClick}>
					{button.label}
				</button>
			);
		});

	const getApplicantStage = value =>
		applicantStore.stageOptions.find(stage => stage.value === value);

	const getApplicantStatus = value =>
		applicantStore.statusOptions.find(status => status.value === value);

	const handleApplicantStageChange = async newStage => {
		const result = await applicantStore.update(applicantId, {
			applicant: { stage: newStage }
		});
		handleSuccess();

		if (result?.status === 'success') {
			eventManager.emit('applicantStageChanged', { applicantId });
		}

		offerJobApplicationCascade({
			newStage,
			applicantIds: applicantId,
			preloadedJobApplications: data?.jobApplications,
			onSuccess: handleSuccess
		});
	};

	const getEnglishLevel = value =>
		candidatesStore.englishLevelOptions.find(level => level.value === value);
	const getLookingForJob = value =>
		candidatesStore.lookingForJobOptions.find(option => option.value === value);
	const getHireability = value =>
		candidateHiringOptions.find(option => option.value === value);

	const menuItems = [
		hasPermission(PERMISSIONS.APPLICANT_EDIT) && {
			label: 'Edit',
			onClick: () => openApplicantEditModal()
		},
		hasPermission(PERMISSIONS.APPLICANT_STAGE_CHANGE) && {
			label: 'Change Internal Stage',
			subItems: applicantStore.stageOptions
				.filter(stage => stage.value !== data?.stage)
				.map(stage => {
					const statusData = getApplicantStatus(
						applicantStore.stageToStatus[stage.value]
					);

					return {
						...stage,
						label: (
							<div className="is-flex is-align-items-center gap-2">
								<ApplicantStageIndicator
									stageLabel={stage?.label}
									statusLabel={statusData?.label}
									stageLevel={stage?.level}
									statusColor={statusData?.color}
								/>
								{stage.label}
							</div>
						),
						onClick: () => handleApplicantStageChange(stage.value)
					};
				})
		}
	].filter(Boolean);

	const applicantStageData = getApplicantStage(data?.stage);
	const applicantStatusData = getApplicantStatus(data?.status);
	const englishLevelData = getEnglishLevel(data?.englishLevel);
	const hireabilityData = getHireability(data?.hireability);
	const isHireabilityEmpty = !data?.hireability;
	const lookingForJobData = getLookingForJob(data?.candidate?.lookingForJob);

	const expectedHourlyCompensation = data?.compensations
		?.filter(
			compensation =>
				compensation.scheme === 'hourly' && compensation.type === 'expected'
		)
		?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

	const expectedMonthlyCompensation = data?.compensations
		?.filter(
			compensation =>
				compensation.scheme === 'monthly' && compensation.type === 'expected'
		)
		?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

	const currentHourlyCompensation = data?.compensations
		?.filter(
			compensation =>
				compensation.scheme === 'hourly' && compensation.type === 'current'
		)
		?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

	const currentMonthlyCompensation = data?.compensations
		?.filter(
			compensation =>
				compensation.scheme === 'monthly' && compensation.type === 'current'
		)
		?.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

	const primaryJobRole = data?.potentialJobRoles?.find(role => role.isPrimary);
	const otherJobRoles = data?.potentialJobRoles?.filter(
		role => !role.isPrimary
	);

	return (
		<div className="ApplicantProfile">
			<div className="drawer-header">
				<div className="applicant-drawer-header-details">
					<div className="is-flex gap-4">
						<div className="applicant-source">
							<span>Source:</span>
							{data?.source ? (
								<UserProfileAvatar
									name={data?.source?.name}
									imageUrl={data?.source?.imageUrl}
									showName
								/>
							) : (
								<span>N/A</span>
							)}
						</div>
						<div className="applicant-referrer">
							<span>Referrer:</span>
							{data?.referrer ? (
								<UserProfileAvatar
									name={data?.referrer?.name}
									imageUrl={data?.referrer?.imageUrl}
									showName
								/>
							) : (
								<span>N/A</span>
							)}
						</div>
						<div className="applicant-owner">
							<span>Owner:</span>
							{data?.owner ? (
								<UserProfileAvatar
									name={data?.owner?.name}
									imageUrl={data?.owner?.imageUrl}
									showName
								/>
							) : (
								<span>N/A</span>
							)}
						</div>
					</div>
					<div className="is-flex gap-4">
						<div className="applicant-last-engagement">
							<span>Last Engagement:</span>
							{data?.lastEngagementAt ? (
								<span className="applicant-last-engagement-info">
									<UserProfileAvatar
										name={data?.lastEngagementBy?.name}
										imageUrl={data?.lastEngagementBy?.imageUrl}
										tooltipPosition="bottom"
									/>
									{formatTimeAgo(new Date(data?.lastEngagementAt), {
										moreThanOneDay: true
									})}
								</span>
							) : (
								<span>N/A</span>
							)}
						</div>
						{menuItems.length > 0 && (
							<HoverMenu
								position="left"
								buttonIcon={
									<img src="/ats/icons/three-dots-icon.svg" alt="Menu button" />
								}
								items={menuItems}
							/>
						)}
					</div>
				</div>

				<button
					className="drawer-close-btn button-bg-soft-small is-icon-only"
					onClick={() => (onClose ? onClose() : drawerStore.closeDrawer())}>
					<img src="/ats/icons/close-blue-icon.svg" alt="" />
				</button>
			</div>
			<div className="applicant-profile-header">
				<div className="applicant-bio-wrapper">
					<div className="resume-options-button">
						<PermissionGate permission={PERMISSIONS.APPLICANT_RESUME_MANAGE}>
							<HoverMenu
								buttonText="Resume"
								buttonIcon={
									<img
										src="/ats/icons/chevron-down-blue-icon.svg"
										alt="Menu button"
									/>
								}
								items={[
									{
										label: 'View',
										img: '/ats/icons/eye-icon.svg',
										onClick: async () => {
											try {
												const result =
													await applicantStore.getResumes(applicantId);
												if (
													result.status === 'success' &&
													result.data.length > 0
												) {
													const latestResume = result.data[0];
													await handleViewResume(latestResume);
												} else {
													notificationStore.pushErrorNotification(
														'No Resume',
														'No resume found for this applicant.'
													);
												}
											} catch (error) {
												notificationStore.pushErrorNotification(
													'Error',
													'Failed to view resume.'
												);
											}
										}
									},
									{
										label: 'Download',
										img: '/ats/icons/download-icon.svg',
										onClick: async () => {
											try {
												const result =
													await applicantStore.getResumes(applicantId);
												if (
													result.status === 'success' &&
													result.data.length > 0
												) {
													const latestResume = result.data[0];
													await handleDownloadResume(latestResume);
												} else {
													notificationStore.pushErrorNotification(
														'No Resume',
														'No resume found for this applicant.'
													);
												}
											} catch (error) {
												notificationStore.pushErrorNotification(
													'Error',
													'Failed to download resume.'
												);
											}
										}
									},
									{
										label: 'History',
										img: '/ats/icons/history-icon.svg',
										onClick: () => {
											modalStore.openModal(
												{
													title: 'Resume',
													size: 'md',
													position: 'center'
												},
												<ApplicantResumeHistory
													applicantId={applicantId}
													handleSuccess={handleSuccess}
													onView={handleViewResume}
													onDownload={handleDownloadResume}
												/>
											);
										}
									},
									{
										label: 'Upload',
										img: '/ats/icons/upload-icon.svg',
										onClick: () => {
											modalStore.openModal(
												{
													title: 'Upload Resume',
													size: 'md',
													position: 'center',
													saveButtonLabel: 'Upload'
												},
												<ApplicantResumeUpload
													applicantId={applicantId}
													handleSuccess={handleSuccess}
												/>
											);
										}
									}
								]}
							/>
						</PermissionGate>
					</div>
					<ProfileImage
						className="profile-picture"
						src={data?.imageUrl || data?.candidate?.imageUrl}
						size={96}
						name={data?.name}
					/>
					<div className="applicant-name-wrapper">
						<HoverMenu
							position="left"
							menuClassName="applicant-stage-menu-items"
							disabled={!hasPermission(PERMISSIONS.APPLICANT_STAGE_CHANGE)}
							buttonIcon={
								<>
									<ApplicantStageIndicator
										stageLabel={applicantStageData?.label}
										statusLabel={applicantStatusData?.label}
										stageLevel={applicantStageData?.level}
										statusColor={applicantStatusData?.color}
										tooltipPosition="bottom"
									/>
									{hasPermission(PERMISSIONS.APPLICANT_STAGE_CHANGE) && (
										<img
											className="chevron-down-icon"
											src="/ats/icons/chevron-down-icon.svg"
											alt=""
										/>
									)}
								</>
							}
							items={applicantStore.stageOptions
								.filter(stage => stage.value !== data?.stage)
								.map(stage => {
									const statusData = getApplicantStatus(
										applicantStore.stageToStatus[stage.value]
									);

									return {
										...stage,
										label: (
											<div className="is-flex is-align-items-center gap-2">
												<ApplicantStageIndicator
													stageLabel={stage?.label}
													statusLabel={statusData?.label}
													stageLevel={stage?.level}
													statusColor={statusData?.color}
												/>
												{stage.label}
											</div>
										),
										onClick: () => handleApplicantStageChange(stage.value)
									};
								})}
						/>
						<h3>{data?.name}</h3>
					</div>
					<h5 className="applicant-role">
						{primaryJobRole?.jobRole?.name || 'N/A'}
						{otherJobRoles?.length > 0 ? (
							<span
								className="tooltip is-tooltip-top"
								data-tooltip={otherJobRoles
									.map(role => role.jobRole?.name || 'N/A')
									.join(' | ')}>
								+{otherJobRoles.length}
							</span>
						) : null}
					</h5>
					<div className="applicant-tags">
						{data?.applicantTags?.slice(0, 3).map(({ tag }) => {
							const featuredTag = applicantStore.featuredTagsOptions.find(
								option => option === tag.name
							);

							const featuredTagIcon = featuredTag
								? applicantStore.featuredTagsIcons[featuredTag.toLowerCase()]
								: null;

							return (
								<span key={tag.id} className="applicant-tag">
									{featuredTagIcon && (
										<img
											src={`/ats/icons/${featuredTagIcon}.svg`}
											alt={featuredTag}
										/>
									)}
									{tag.name}
								</span>
							);
						})}
						{data?.applicantTags?.length > 3 && (
							<span
								className="tooltip is-tooltip-top"
								data-tooltip={data?.applicantTags
									?.slice(3)
									.map(({ tag }) => tag.name)
									.join(' | ')}>
								+{data?.applicantTags?.length - 3}
							</span>
						)}
					</div>
					<div className="bio-top-right-info">
						{lookingForJobData?.value === 'yes' && (
							<span className="looking-for-a-job-status">
								{lookingForJobData?.text || 'N/A'}
							</span>
						)}
					</div>
				</div>
				<div className="applicant-profile-informations">
					<div className="applicant-profile-information-box">
						<img src="/ats/icons/location-icon.svg" alt="Location" />
						<strong
							className={
								[data?.country?.name, data?.city].filter(Boolean).length === 0
									? 'is-empty'
									: ''
							}>
							{[data?.country?.name, data?.city].filter(Boolean).join(', ') ||
								'Empty'}
						</strong>
					</div>
					<div className="applicant-profile-information-box">
						<img src="/ats/icons/message-icon.svg" alt="English Level" />
						<strong className={!englishLevelData?.text ? 'is-empty' : ''}>
							{englishLevelData?.text || 'Empty'}
						</strong>
					</div>
					<div className="applicant-profile-information-box">
						<img src="/ats/icons/dashboard-2-icon.svg" alt="Seniority" />
						<strong
							className={
								!candidateSeniorityOptionLabel[data?.seniority]
									? 'is-empty'
									: ''
							}>
							{candidateSeniorityOptionLabel[data?.seniority] || 'Empty'}
						</strong>
					</div>
					{isHireabilityEmpty ? (
						<button
							type="button"
							className="applicant-profile-information-box hireability-status hireability-status-empty"
							onClick={() => openApplicantEditModal('hireability')}>
							<img src="/ats/icons/hireability-empty-icon.svg" alt="" />
							<span>Set Hireability</span>
						</button>
					) : (
						<div
							className={`applicant-profile-information-box hireability-status hireability-status-${hireabilityData?.value}`}>
							<img
								src={`/ats/icons/face-${
									hireabilityData?.value === 'yes'
										? 'happy'
										: hireabilityData?.value === 'no'
											? 'sad'
											: 'neutral'
								}-icon.svg`}
								alt={hireabilityData?.text || 'Empty'}
							/>
							<span>
								{hireabilityData?.value === 'yes'
									? 'Hireable'
									: hireabilityData?.value === 'no'
										? 'Not Hireable'
										: 'Maybe'}
							</span>
						</div>
					)}
				</div>
				<div className="applicant-profile-informations">
					<div className="applicant-profile-information-box">
						<img src="/ats/icons/dollar-icon.svg" alt="Compensation" />
						<span>Expected</span>
						<strong className={!expectedHourlyCompensation ? 'is-empty' : ''}>
							{expectedHourlyCompensation
								? `${expectedHourlyCompensation?.currency?.symbol}${expectedHourlyCompensation?.amount}/Hr`
								: 'Empty'}
						</strong>
						<div className="separator"></div>
						<strong className={!expectedMonthlyCompensation ? 'is-empty' : ''}>
							{expectedMonthlyCompensation
								? `${expectedMonthlyCompensation?.currency?.symbol}${expectedMonthlyCompensation?.amount}/Mo`
								: 'Empty'}
						</strong>
					</div>
					<div className="applicant-profile-information-box">
						<img src="/ats/icons/dollar-icon.svg" alt="Compensation" />
						<span>Current</span>
						<strong className={!currentHourlyCompensation ? 'is-empty' : ''}>
							{currentHourlyCompensation
								? `${currentHourlyCompensation?.currency?.symbol}${currentHourlyCompensation?.amount}/Hr`
								: 'Empty'}
						</strong>
						<div className="separator"></div>
						<strong className={!currentMonthlyCompensation ? 'is-empty' : ''}>
							{currentMonthlyCompensation
								? `${currentMonthlyCompensation?.currency?.symbol}${currentMonthlyCompensation?.amount}/Mo`
								: 'Empty'}
						</strong>
					</div>
					<div className="applicant-profile-information-box applicant-social-links">
						{data?.phoneNumber ? (
							<ActionMenu
								buttonIcon={<img src="/ats/icons/phone-icon.svg" alt="Phone" />}
								tooltipText={data?.phoneNumber}
								items={[
									{
										label: 'Copy Phone number',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(data?.phoneNumber),
										successLabel: 'Phone Number Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Make a Phone Call',
										img: '/ats/icons/send-icon.svg',
										onClick: () =>
											window.open(`tel:${data?.phoneNumber}`, '_blank')
									}
								]}
							/>
						) : (
							<div className="inactive-icon">
								<img src="/ats/icons/phone-icon.svg" alt="Phone" />
							</div>
						)}
						{data?.email ? (
							<ActionMenu
								buttonIcon={<img src="/ats/icons/email-icon.svg" alt="Email" />}
								tooltipText={data?.email}
								items={[
									{
										label: 'Copy Email',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(data?.email),
										successLabel: 'Email Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Send an Email',
										img: '/ats/icons/send-icon.svg',
										onClick: () =>
											window.open(`mailto:${data?.email}`, '_blank')
									}
								]}
							/>
						) : (
							<div className="inactive-icon">
								<img src="/ats/icons/email-icon.svg" alt="Email" />
							</div>
						)}
						{data?.githubUrl ? (
							<ActionMenu
								buttonIcon={
									<img src="/ats/icons/github-icon.svg" alt="GitHub" />
								}
								tooltipText={data?.githubUrl}
								items={[
									{
										label: 'Copy GitHub URL',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(data?.githubUrl),
										successLabel: 'GitHub URL Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Open GitHub URL',
										img: '/ats/icons/send-icon.svg',
										onClick: () => window.open(data?.githubUrl, '_blank')
									}
								]}
							/>
						) : (
							<div className="inactive-icon">
								<img src="/ats/icons/github-icon.svg" alt="GitHub" />
							</div>
						)}
						{data?.linkedinUrl ? (
							<ActionMenu
								buttonIcon={
									<img src="/ats/icons/linkedin-icon.svg" alt="LinkedIn" />
								}
								tooltipText={data?.linkedinUrl}
								items={[
									{
										label: 'Copy LinkedIn URL',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(data?.linkedinUrl),
										successLabel: 'LinkedIn URL Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Open LinkedIn URL',
										img: '/ats/icons/send-icon.svg',
										onClick: () => window.open(data?.linkedinUrl, '_blank')
									}
								]}
							/>
						) : (
							<div className="inactive-icon">
								<img src="/ats/icons/linkedin-icon.svg" alt="LinkedIn" />
							</div>
						)}
						{data?.portfolioUrl ? (
							<ActionMenu
								buttonIcon={
									<img src="/ats/icons/globe-2-icon.svg" alt="Portfolio" />
								}
								tooltipText={data?.portfolioUrl}
								items={[
									{
										label: 'Copy Portfolio URL',
										img: '/ats/icons/copy-icon.svg',
										onClick: () => copyTextToClipboard(data?.portfolioUrl),
										successLabel: 'Portfolio URL Copied',
										successImg: '/ats/icons/check-icon.svg',
										resetDelay: 2000
									},
									{
										label: 'Open Portfolio URL',
										img: '/ats/icons/send-icon.svg',
										onClick: () => window.open(data?.portfolioUrl, '_blank')
									}
								]}
							/>
						) : (
							<div className="inactive-icon">
								<img src="/ats/icons/globe-2-icon.svg" alt="Portfolio" />
							</div>
						)}
					</div>
				</div>
			</div>
			<div className="applicant-profile-content">
				<div className="tabbed-panel">
					<div className="tabs">
						<ul>
							{isApplicantProfileTabAllowed('internal_process') && (
								<li
									className={
										'tabbed-panel-option ' +
										(activeTab === TABS.INTERNAL_PROCESS ? 'is-active' : '')
									}>
									<a
										href="void"
										onClick={e => handleTabClick(e, TABS.INTERNAL_PROCESS)}>
										<span>Internal Process</span>
										<PendingTaskBadge
											count={internalProcessPendingCount}
											variant="dot"
											tooltipPosition="right"
										/>
									</a>
								</li>
							)}
							{isApplicantProfileTabAllowed('history') && (
								<li
									className={
										'tabbed-panel-option ' +
										(activeTab === TABS.HISTORY ? 'is-active' : '')
									}>
									<a href="void" onClick={e => handleTabClick(e, TABS.HISTORY)}>
										<span>Job History</span>
										<PendingTaskBadge
											count={jobHistoryPendingCount}
											variant="dot"
											tooltipPosition="right"
										/>
									</a>
								</li>
							)}
							{isApplicantProfileTabAllowed('profile') && (
								<li
									className={
										'tabbed-panel-option ' +
										(activeTab === TABS.PROFILE ? 'is-active' : '')
									}>
									<a href="void" onClick={e => handleTabClick(e, TABS.PROFILE)}>
										<span>Profile</span>
									</a>
								</li>
							)}
							{isApplicantProfileTabAllowed('changelog') && (
								<li
									className={
										'tabbed-panel-option ' +
										(activeTab === TABS.CHANGELOG ? 'is-active' : '')
									}>
									<a
										href="void"
										onClick={e => handleTabClick(e, TABS.CHANGELOG)}>
										<span>Log</span>
									</a>
								</li>
							)}
						</ul>
					</div>
					<div id="tabs-content-wrapper" className="tabs-content-wrapper">
						{isApplicantProfileTabAllowed('history') && (
							<div
								className={`box tabbed-panel-content ${activeTab === TABS.HISTORY ? 'is-active' : ''}`}
								id="history">
								<div className="applicant-tab-section-header">
									<div className="applicant-tab-section-title">
										{TAB_SECTION_TITLES[TABS.HISTORY]}
									</div>
									<div className="table-action-buttons">
										{data?.jobApplications?.length > 0 && (
											<GroupByPositionToggle
												value={groupJobHistoryByPosition}
												onChange={handleGroupJobHistoryByPositionChange}
											/>
										)}
										{renderTabActionButtons(TABS.HISTORY)}
									</div>
								</div>
								<ApplicantHistory
									applicant={data}
									onSuccess={handleSuccess}
									expandedJobApplicationId={jobApplicationId}
									groupByPosition={groupJobHistoryByPosition}
								/>
							</div>
						)}
						{isApplicantProfileTabAllowed('internal_process') && (
							<div
								className={`box tabbed-panel-content ${activeTab === TABS.INTERNAL_PROCESS ? 'is-active' : ''}`}
								id="internal-process">
								<div className="applicant-tab-section-header">
									<div className="applicant-tab-section-title">
										{TAB_SECTION_TITLES[TABS.INTERNAL_PROCESS]}
									</div>
									<div className="table-action-buttons">
										<PinnedPanelToggleButton scope={PIN_SCOPES.APPLICANT} />
										{renderTabActionButtons(TABS.INTERNAL_PROCESS)}
									</div>
								</div>
								<ApplicantInternalProcess
									applicant={data}
									onSuccess={handleSuccess}
									onAssignToJob={assignApplicantToJob}
								/>
							</div>
						)}
						{isApplicantProfileTabAllowed('profile') && (
							<div
								className={`box tabbed-panel-content ${activeTab === TABS.PROFILE ? 'is-active' : ''}`}
								id="profile">
								<div className="applicant-tab-section-header">
									<div className="applicant-tab-section-title">
										{TAB_SECTION_TITLES[TABS.PROFILE]}
									</div>
									<div className="table-action-buttons">
										{renderTabActionButtons(TABS.PROFILE)}
									</div>
								</div>
								<ApplicantPublicProfile
									applicant={data}
									onSuccess={handleSuccess}
								/>
							</div>
						)}
						{isApplicantProfileTabAllowed('changelog') && (
							<div
								className={`box tabbed-panel-content ${activeTab === TABS.CHANGELOG ? 'is-active' : ''}`}
								id="changelog">
								<ApplicantChangelog
									applicant={data}
									onSuccess={handleSuccess}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
